import { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { getAvatar } from 'utils/helpers';
import { useStore } from '../../store';
import Dropdown from 'components/Base/Dropdown';
import IconLogo from './img/logo.svg';
import {
	MagnifyingGlassIcon,
	UserCircleIcon,
	ChatBubbleLeftIcon,
	CalendarIcon,
	Bars3Icon,
	XMarkIcon,
	ChevronDownIcon,
	UserGroupIcon,
	ArrowRightOnRectangleIcon,
	BuildingOfficeIcon,
	UserIcon,
	PlusIcon,

} from "@heroicons/react/24/outline";

const Header = () => {
	const [searchType, setSearchType] = useState('Caregiver');
	const [showDropdown, setShowDropdown] = useState(false);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [showStickyHeader, setShowStickyHeader] = useState(false);

	const store = useStore();
	const navigate = useNavigate();

	const { isAuth, user, accessToPlatform } = store.auth;

	useEffect(() => {
		setSearchType(user.isCustomer ? 'Caregiver' : 'Job');
	}, [user]);

	useEffect(() => {
		const handleScroll = () => {
			const scrollPosition = window.scrollY;
			setShowStickyHeader(scrollPosition > 200);
		};

		window.addEventListener("scroll", handleScroll);
		return () => window.removeEventListener("scroll", handleScroll);
	}, []);

	const toggleDropdown = useCallback((event) => {
		event.stopPropagation();
		setShowDropdown(prev => !prev);
	}, []);

	useEffect(() => {
		const closeHandler = () => {
			setShowDropdown(false);
		};

		document.addEventListener('click', closeHandler);
		return () => document.removeEventListener('click', closeHandler);
	}, []);

	const showSignIn = useCallback(() => {
		store.auth.toggleModal(true);
	}, [store.auth]);

	const signOut = useCallback(() => {
		store.auth.unauthorize();
	}, [store.auth]);

	const handleSearch = useCallback(
		(e) => {
			e.preventDefault();
			const value = e.target.search.value;
			const page = searchType === 'Job' ? '/jobs' : '/providers';

			navigate(page, {
				state: {
					search: value,
				},
			});

			e.target.search.value = '';
		},
		[searchType, navigate]
	);

	return (
		<>
			{/* Sticky Header */}
			<div
				className={`fixed inset-x-0 top-4 z-50 flex justify-center transition-all duration-300 ${showStickyHeader ? "translate-y-0 opacity-100" : "-translate-y-full opacity-0"
					}`}
			>
				<div className="w-full max-w-[1100px] mx-4 flex items-center justify-between rounded-full bg-white/80 backdrop-blur-md shadow-md shadow-sky-900/5 ring-1 ring-slate-200 px-5 py-4">
					<Link to="/" className="flex-shrink-0">
						<img src={IconLogo} alt="Emissary Care" className="h-8 w-auto" />
					</Link>

					{isAuth ? (
						<div className="flex items-center gap-4">
							{/* {user.isProvider && accessToPlatform && (
								<Link to="/bids" className="hidden md:block text-sm font-medium text-slate-600 hover:text-blue-600">
									My Jobs
								</Link>
							)} */}

							{/* {user.isPartner && (
								<Link
									to="/providers"
									className="hidden md:flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 relative"
								>
									<UserIcon className="h-5 w-5 mr-1.5" />
									Find caregivers
								</Link>
							)} */}


							{/* {accessToPlatform && (
								<Link
									to="/chats"
									className="hidden md:flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 relative"
								>
									<ChatBubbleLeftIcon className="h-5 w-5 mr-1.5" />
									Chats
									{store.notif.unreadedChats && (
										<span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full" />
									)}
								</Link>
							)} */}


							{/* {user.isCustomer && (
								<Link
									to="/jobs/my"
									className="hidden md:flex items-center text-sm font-medium text-slate-600 hover:text-blue-600"
								>
									<CalendarIcon className="h-5 w-5 mr-1.5" />
									My Visits
								</Link>
							)} */}

							<Link
								to="/jobs/athena"
								className="hidden md:flex items-center text-sm font-medium text-slate-600 hover:text-blue-600"
							>
								<CalendarIcon className="h-5 w-5 mr-1.5" />
								My appointments
							</Link>


							<div className="relative">
								<button
									onClick={toggleDropdown}
									className="hidden md:flex items-center text-sm focus:outline-none"
									id="user-menu-button"
								>
									<div className="flex-shrink-0 h-8 w-8 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
										<img src={getAvatar(user.avatar)} alt={user.name} className="h-full w-full object-cover" />
									</div>
									<div className="hidden md:block ml-2 text-left">
										<div className="text-xs text-slate-500">{user.type}</div>
										<div className="text-sm font-medium text-slate-700 truncate max-w-[100px]">
											{user.name}</div>
									</div>
									<ChevronDownIcon className={`ml-1 h-5 w-5 text-slate-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
								</button>

								{/* Avatar only (non-clickable) for mobile */}
								<div className="md:hidden flex-shrink-0 h-8 w-8 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
									<img src={getAvatar(user.avatar)} alt={user.name} className="h-full w-full object-cover" />
								</div>

								{showDropdown && (
									<div className="hidden md:block absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
										<div className="py-1" role="none">
											<Link
												to="/profile"
												className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
											>
												<UserCircleIcon className="mr-3 h-5 w-5 text-slate-400" />
												My Profile
											</Link>

											{/* {user.isProvider && (
												<Link
													to="/bids"
													className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
												>
													<CalendarIcon className="mr-3 h-5 w-5 text-slate-400" />
													My Jobs
												</Link>
											)} */}

											{/* {user.isCustomer && (
												<Link
													to="/jobs/my"
													className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
												>
													<CalendarIcon className="mr-3 h-5 w-5 text-slate-400" />
													My Visits
												</Link>
											)} */}

											<Link
												to="/jobs/athena"
												className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
											>
												<CalendarIcon className="mr-3 h-5 w-5 text-slate-400" />
												My appointments
											</Link>

											{user.isEligibleAthena ? (
												<Link
													to="/jobs/create/athena"
													className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
												>
													<PlusIcon className="mr-3 h-5 w-5 text-slate-400" />
													New appointment
												</Link>
											) : (
												<div className="flex items-center px-4 py-2 text-sm text-slate-400 cursor-not-allowed">
													<PlusIcon className="mr-3 h-5 w-5 text-slate-300" />
													<span className="text-xs">You could schedule an appointment once your information is verified</span>
												</div>
											)}

											<div className="border-t border-slate-100 my-1"></div>

											<button
												onClick={signOut}
												className="flex w-full items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
											>
												<ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-slate-400" />
												Sign Out
											</button>
										</div>
									</div>
								)}
							</div>

							<button
								onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
								className="inline-flex md:hidden items-center justify-center p-2 rounded-md text-slate-400 hover:text-slate-600 focus:outline-none"
							>
								{mobileMenuOpen ? (
									<XMarkIcon className="block h-6 w-6" aria-hidden="true" />
								) : (
									<Bars3Icon className="block h-6 w-6" aria-hidden="true" />
								)}
							</button>
						</div>
					) : (
						<div className="flex items-center space-x-4">
							<div className="hidden md:flex space-x-6">
								<Link to="/about" className="text-sm font-medium text-slate-600 hover:text-blue-600">
									About Us
								</Link>
								<Link to="/about/caregivers" className="text-sm font-medium text-slate-600 hover:text-blue-600">
									For Caregivers
								</Link>
								<Link to="/about/how" className="text-sm font-medium text-slate-600 hover:text-blue-600">
									How it works
								</Link>
							</div>

							<div className="hidden md:flex items-center space-x-4">
								<button
									onClick={showSignIn}
									className="text-sm font-medium text-slate-600 hover:text-blue-600"
								>
									Log in
								</button>
								<button
									onClick={() => navigate('/auth')}
									className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
								>
									Sign up
								</button>
							</div>

							<button
								onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
								className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-slate-600 focus:outline-none"
							>
								{mobileMenuOpen ? (
									<XMarkIcon className="block h-6 w-6" aria-hidden="true" />
								) : (
									<Bars3Icon className="block h-6 w-6" aria-hidden="true" />
								)}
							</button>
						</div>
					)}
				</div>
			</div>

			{/* Regular Header */}
			<header className="bg-white border-b border-slate-200 shadow-sm">
				<div className="mx-auto" style={{ maxWidth: '1100px' }}>
					<div className="flex justify-between items-center h-16">
						<Link to="/" className="flex-shrink-0">
							<img src={IconLogo} alt="Emissary Care" className="h-10 w-auto" />
						</Link>

						{isAuth ? (
							<div className="flex items-center gap-4">
								{/* {user.isCustomer && accessToPlatform && (
								<div className="hidden md:block relative">
									<form onSubmit={handleSearch} className="flex items-center rounded-lg bg-slate-50 p-1">
										<div className="pointer-events-none px-2">
											<MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
										</div>
										<input
											name="search"
											type="text"
											placeholder="I'm looking for..."
											className="block w-full bg-transparent border-none py-1.5 pl-1 pr-2 text-sm placeholder-slate-400 focus:ring-0"
										/>
										<Dropdown items={['Caregiver']} onChange={setSearchType}>
											<span className="flex items-center px-3 py-1.5 text-sm font-medium text-slate-700 bg-white rounded-md shadow-sm border border-slate-200">
												{searchType}
												<ChevronDownIcon className="ml-1 h-4 w-4 text-slate-400" />
											</span>
										</Dropdown>
									</form>
								</div>
							)} */}

								{/* {user.isProvider && accessToPlatform && (
									<Link to="/bids" className="hidden md:block text-sm font-medium text-slate-600 hover:text-blue-600">
										My Jobs
									</Link>
								)} */}

								{/* {user.isPartner && (
									<Link
										to="/providers"
										className="hidden md:flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 relative"
									>
										<UserIcon className="h-5 w-5 mr-1.5" />
										Find caregivers
									</Link>
								)} */}

								{/* {accessToPlatform && (
									<Link
										to="/chats"
										className="hidden md:flex items-center text-sm font-medium text-slate-600 hover:text-blue-600 relative"
									>
										<ChatBubbleLeftIcon className="h-5 w-5 mr-1.5" />
										Chats
										{store.notif.unreadedChats && (
											<span className="absolute -top-1 -right-1 h-2 w-2 bg-red-500 rounded-full" />
										)}
									</Link>
								)} */}

								{/* {user.isCustomer && (
									<Link
										to="/jobs/my"
										className="hidden md:flex items-center text-sm font-medium text-slate-600 hover:text-blue-600"
									>
										<CalendarIcon className="h-5 w-5 mr-1.5" />
										My Visits
									</Link>
								)} */}

								{user.isCustomer && (
									<Link
										to="/jobs/athena"
										className="hidden md:flex items-center text-sm font-medium text-slate-600 hover:text-blue-600"
									>
										<CalendarIcon className="h-5 w-5 mr-1.5" />
										My appointments
									</Link>
								)}


								{/* Mobile actions */}
								{/* <div className="md:hidden flex items-center">
									{accessToPlatform && (

										<Link to="/chats" className="p-2 relative">
											<ChatBubbleLeftIcon className="h-6 w-6 text-slate-500" />
											{store.notif.unreadedChats && (
												<span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full" />
											)}
										</Link>
									)}
								</div> */}

								<div className="relative">
									<button
										onClick={toggleDropdown}
										className="hidden md:flex items-center text-sm focus:outline-none"
										id="user-menu-button"
									>
										<div className="flex-shrink-0 h-8 w-8 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
											<img src={getAvatar(user.avatar)} alt={user.name} className="h-full w-full object-cover" />
										</div>
										<div className="hidden md:block ml-2 text-left">
											<div className="text-xs text-slate-500">{user.type}</div>
											<div className="text-sm font-medium text-slate-700 truncate ">
												<span className='text-sm font-normal'>
													Welcome back,
												</span>&nbsp;
												{user.name}
											</div>
										</div>
										<ChevronDownIcon className={`ml-1 h-5 w-5 text-slate-400 transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`} />
									</button>

									{/* Avatar only (non-clickable) for mobile */}
									<div className="md:hidden flex-shrink-0 h-8 w-8 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
										<img src={getAvatar(user.avatar)} alt={user.name} className="h-full w-full object-cover" />
									</div>

									{showDropdown && (
										<div className="hidden md:block absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50">
											<div className="py-1" role="none">
												<Link
													to="/profile"
													className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
												>
													<UserCircleIcon className="mr-3 h-5 w-5 text-slate-400" />
													My Profile
												</Link>

												{user.isProvider && (
													<Link
														to="/bids"
														className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
													>
														<CalendarIcon className="mr-3 h-5 w-5 text-slate-400" />
														My Jobs
													</Link>
												)}

												{/* {user.isCustomer && (
													<Link
														to="/jobs/my"
														className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
													>
														<CalendarIcon className="mr-3 h-5 w-5 text-slate-400" />
														My Visits
													</Link>
												)} */}

												{user.isCustomer && (
													<Link
														to="/jobs/athena"
														className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
													>
														<CalendarIcon className="mr-3 h-5 w-5 text-slate-400" />
														My appointments
													</Link>
												)}

												{user.isEligibleAthena ? (
													<Link
														to="/jobs/create/athena"
														className="flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
													>
														<PlusIcon className="mr-3 h-5 w-5 text-slate-400" />
														New appointment
													</Link>
												) : (
													<div className="flex items-center px-4 py-2 text-sm text-slate-400 cursor-not-allowed">
														<PlusIcon className="mr-3 h-5 w-5 text-slate-300" />
														<span className="text-xs">You could schedule an appointment once your information is verified</span>
													</div>
												)
												}

												<div className="border-t border-slate-100 my-1"></div>

												<button
													onClick={signOut}
													className="flex w-full items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
												>
													<ArrowRightOnRectangleIcon className="mr-3 h-5 w-5 text-slate-400" />
													Sign Out
												</button>
											</div>
										</div>
									)}
								</div>

								<button
									onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
									className="inline-flex md:hidden items-center justify-center p-2 rounded-md text-slate-400 hover:text-slate-600 focus:outline-none"
								>
									{mobileMenuOpen ? (
										<XMarkIcon className="block h-6 w-6" aria-hidden="true" />
									) : (
										<Bars3Icon className="block h-6 w-6" aria-hidden="true" />
									)}
								</button>
							</div>
						) : (
							<div className="flex items-center space-x-4">
								<div className="hidden md:flex space-x-6">
									<Link to="/about" className="text-sm font-medium text-slate-600 hover:text-blue-600">
										About Us
									</Link>
									<Link to="/about/caregivers" className="text-sm font-medium text-slate-600 hover:text-blue-600">
										For Caregivers
									</Link>
									<Link to="/about/how" className="text-sm font-medium text-slate-600 hover:text-blue-600">
										How it works
									</Link>
								</div>

								<div className="hidden md:flex items-center space-x-4">
									<button
										onClick={showSignIn}
										className="text-sm font-medium text-slate-600 hover:text-blue-600"
									>
										Log in
									</button>
									<button
										onClick={() => navigate('/auth')}
										className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
									>
										Sign up
									</button>
								</div>

								<button
									onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
									className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-slate-400 hover:text-slate-600 focus:outline-none"
								>
									{mobileMenuOpen ? (
										<XMarkIcon className="block h-6 w-6" aria-hidden="true" />
									) : (
										<Bars3Icon className="block h-6 w-6" aria-hidden="true" />
									)}
								</button>
							</div>
						)}
					</div>
				</div>
			</header>

			{/* Mobile menu */}
			{mobileMenuOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto bg-slate-900/25 backdrop-blur-sm" onClick={() => setMobileMenuOpen(false)}>
					<div className="fixed inset-y-0 right-0 max-w-[80%] w-full bg-white shadow-xl" onClick={e => e.stopPropagation()}>
						<div className="flex items-center justify-between px-4 h-16 border-b border-slate-200">
							<Link to="/" className="flex-shrink-0">
								<img src={IconLogo} alt="Emissary Care" className="h-8 w-auto" />
							</Link>
							<button
								className="rounded-md p-1 text-slate-400 hover:text-slate-500"
								onClick={() => setMobileMenuOpen(false)}
							>
								<XMarkIcon className="h-6 w-6" />
							</button>
						</div>

						<div className="px-2 py-4 space-y-1">
							{isAuth ? (
								<>
									<div className="flex items-center px-3 py-3 border-b border-slate-100 mb-2">
										<div className="h-10 w-10 rounded-full overflow-hidden bg-slate-100 border border-slate-200">
											<img src={getAvatar(user.avatar)} alt={user.name} className="h-full w-full object-cover" />
										</div>
										<div className="ml-3 text-left">
											<div className="text-sm font-medium text-slate-700">
												<span className='text-sm font-normal'>
													Welcome back,
												</span>&nbsp;{user.name}
											</div>
											<div className="text-xs text-slate-500">{user.type}</div>
										</div>
									</div>

									<Link
										to="/profile"
										className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
										onClick={() => setMobileMenuOpen(false)}
									>
										<UserCircleIcon className="mr-3 h-6 w-6 text-slate-400" />
										My Profile
									</Link>

									{/* {user.isProvider && accessToPlatform && (
										<Link
											to="/bids"
											className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
											onClick={() => setMobileMenuOpen(false)}
										>
											<UserGroupIcon className="mr-3 h-6 w-6 text-slate-400" />
											My Jobs
										</Link>
									)} */}
									{/* 
									{accessToPlatform && (
										<Link
											to="/chats"
											className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
											onClick={() => setMobileMenuOpen(false)}
										>
											<ChatBubbleLeftIcon className="mr-3 h-6 w-6 text-slate-400" />
											Chats
											{store.notif.unreadedChats && (
												<span className="ml-2 h-2 w-2 bg-red-500 rounded-full" />
											)}
										</Link>
									)} */}

									{/* {user.isCustomer && (
										<Link
											to="/jobs/my"
											className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
											onClick={() => setMobileMenuOpen(false)}
										>
											<CalendarIcon className="mr-3 h-6 w-6 text-slate-400" />
											My Visits
										</Link>
									)} */}

									{user.isCustomer && (
										<Link
											to="/jobs/athena"
											className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
											onClick={() => setMobileMenuOpen(false)}
										>
											<CalendarIcon className="mr-3 h-6 w-6 text-slate-400" />
											My appointments
										</Link>
									)}

									{user.isEligibleAthena ? (
										<Link
											to="/jobs/create/athena"
											className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
											onClick={() => setMobileMenuOpen(false)}
										>
											<PlusIcon className="mr-3 h-6 w-6 text-slate-400" />
											New appointment
										</Link>
									) : (
										<div className="flex items-center px-3 py-3 text-sm text-slate-400 cursor-not-allowed">
											<PlusIcon className="mr-3 h-6 w-6 text-slate-300" />
											<span className="text-xs">You could schedule an appointment once your information is verified</span>
										</div>
									)}

									{/* {user.isPartner && (
										<Link
											to="/providers"
											className="flex items-center px-3 py-3 text-base font-medium text-slate-50 rounded-md"
										>
											<UserIcon className="h-5 w-5 mr-1.5" />
											Find caregivers
										</Link>
									)} */}

									<div className="border-t border-slate-100 my-2"></div>

									<button
										onClick={() => {
											signOut();
											setMobileMenuOpen(false);
										}}
										className="flex w-full items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
									>
										<ArrowRightOnRectangleIcon className="mr-3 h-6 w-6 text-slate-400" />
										Sign Out
									</button>
								</>
							) : (
								<>
									<Link
										to="/about"
										className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
										onClick={() => setMobileMenuOpen(false)}
									>
										<BuildingOfficeIcon className="mr-3 h-6 w-6 text-slate-400" />
										About Us
									</Link>

									<Link
										to="/about/caregivers"
										className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
										onClick={() => setMobileMenuOpen(false)}
									>
										<UserGroupIcon className="mr-3 h-6 w-6 text-slate-400" />
										For Caregivers
									</Link>

									<Link
										to="/about/how"
										className="flex items-center px-3 py-3 text-base font-medium text-slate-600 hover:bg-slate-50 rounded-md"
										onClick={() => setMobileMenuOpen(false)}
									>
										<ChatBubbleLeftIcon className="mr-3 h-6 w-6 text-slate-400" />
										How it works
									</Link>

									<div className="border-t border-slate-100 my-4"></div>

									<div className="flex gap-2 px-3">
										<button
											onClick={() => {
												showSignIn();
												setMobileMenuOpen(false);
											}}
											className="flex-1 text-center py-2.5 px-4 rounded-md text-blue-600 border border-blue-200 bg-blue-50 hover:bg-blue-100"
										>
											Log in
										</button>

										<button
											onClick={() => {
												navigate('/auth');
												setMobileMenuOpen(false);
											}}
											className="flex-1 text-center py-2.5 px-4 rounded-md text-white bg-blue-600 hover:bg-blue-700"
										>
											Sign up
										</button>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default observer(Header);
